import { Controller } from "@hotwired/stimulus"
import Sortable from "sortablejs"
// The server's own list (FileUploader::EXTENSIONS) — the server is the real
// gate; this only pre-flights so users get instant feedback (Issue 41870).
import { UPLOAD_EXTENSIONS as ALLOWED_EXTENSIONS } from "../lib/upload_extensions"

// insp/insv are Insta360 camera files (Quentin 2026-08-04).
const VIDEO_EXTENSIONS = ["mp4","mov","webm","insv"]
const MAX_UPLOAD_SIZE = 250 * 1024 * 1024
const VIDEO_MAX_UPLOAD_SIZE = 500 * 1024 * 1024
// Big site videos / Insta360 captures (Quentin 2026-08-04) go through S3
// multipart upload — only these formats, up to 15GB.
const MULTIPART_EXTENSIONS = [...VIDEO_EXTENSIONS, "insp"]
const MULTIPART_MAX_UPLOAD_SIZE = 15 * 1024 * 1024 * 1024

const CHEVRON_SVG = '<svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="2" stroke="currentColor" class="w-3.5 h-3.5 transition-transform"><path stroke-linecap="round" stroke-linejoin="round" d="m8.25 4.5 7.5 7.5-7.5 7.5"/></svg>'
// MDI folder (filled) — plain folder
const FOLDER_PRIVATE_SVG = '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" class="w-5 h-5"><path d="M10,4H4C2.89,4 2,4.89 2,6V18A2,2 0 0,0 4,20H20A2,2 0 0,0 22,18V8C22,6.89 21.1,6 20,6H12L10,4Z"/></svg>'
// MDI folder-account (filled) — shared folder (same as old app's folder_shared)
const FOLDER_SHARED_SVG = '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" class="w-5 h-5"><path d="M19,17H11V16C11,14.67 13.67,14 15,14C16.33,14 19,14.67 19,16M15,9A2,2 0 0,1 17,11A2,2 0 0,1 15,13A2,2 0 0,1 13,11C13,9.89 13.9,9 15,9M20,6H12L10,4H4C2.89,4 2,4.89 2,6V18A2,2 0 0,0 4,20H20A2,2 0 0,0 22,18V8C22,6.89 21.1,6 20,6Z"/></svg>'

export default class extends Controller {
  static targets = [
    "treeContainer", "treeEmpty", "loadTemplateLink",
    "breadcrumbs", "fileTableWrapper", "fileTableBody", "fileLoading",
    "noFolderSelected", "emptyFiles", "subFolders", "subFoldersList",
    "editFolderBtn", "deleteFolderBtn", "uploadBtn", "expandBtn", "searchInput",
    "folderActivityBtn",
    "folderPanel", "panelDivider",
    // Email viewer modal (.msg / .eml)
    "emailModal", "emailSubject", "emailMeta", "emailAttachments",
    "emailLoading", "emailError", "emailErrorDownload", "emailBody",
    "emailDownload",
    // Add folder modal
    "addFolderModal", "addFolderName",
    "addPrivacyConsultants", "addPrivacyContractors", "addPrivacyOtherVendors", "addPrivacyCollaborators",
    // Edit folder modal
    "editFolderModal", "editFolderName",
    "editPrivacyConsultants", "editPrivacyContractors", "editPrivacyOtherVendors", "editPrivacyCollaborators",
    // Builder share-with-team lists (items 15-16) — replace the role
    // checkboxes on builder projects
    "addShareList", "editShareList",
    // Delete folder modal
    "deleteFolderModal", "deleteFolderMessage",
    // Generate default documents confirm modal
    "reloadStructureModal",
    // Upload modal
    "uploadModal", "uploadDropzone", "fileInput", "folderInput", "uploadFileList",
    "uploadFolderName", "uploadSubmitBtn",
    "notifySection", "notifyToggle", "notifyMembersList",
    // Rename file modal
    "renameFileModal", "renameFileInput",
    // Delete file modal
    "deleteFileModal", "deleteFileMessage",
    // Export modal
    "exportModal", "exportTree", "exportSubmitBtn", "exportSelectAll",
    // § 3.8 Tier 6 — Doc-sync per-attachment history modal
    "syncHistoryModal", "syncHistoryBody"
  ]

  static values = {
    projectId: Number,
    treeData: String,
    companyRole: String,
    // Owner side, or the builder on their own job: may edit, delete and
    // reorder every folder. Anyone else only the folders they made
    // (each tree node's can_manage). The server enforces the same rule.
    canManageAll: Boolean,
    canCreateFolders: Boolean,
    sortAlphabetical: Boolean,
    currentUserId: Number,
    filesUrl: String,
    reStageFolderFilesUrl: String,
    presignUrl: String,
    registerUrl: String,
    presignMultipartUrl: String,
    resumeMultipartUrl: String,
    completeMultipartUrl: String,
    abortMultipartUrl: String,
    newFolderUrl: String,
    updateFolderNameUrl: String,
    removeFolderUrl: String,
    updateFoldersUrl: String,
    destroyAttachmentUrl: String,
    updateFileUrl: String,
    exportDocumentsUrl: String,
    reloadFolderStructureUrl: String,
    notifiableMembersUrl: String,
    saveFolderFilesUrl: String,
    i18nFolderCreated: { type: String, default: "Folder created" },
    i18nFolderUpdated: { type: String, default: "Folder updated" },
    i18nFolderDeleted: { type: String, default: "Folder deleted" },
    i18nFileRenamed: { type: String, default: "File renamed" },
    i18nFileDeleted: { type: String, default: "File deleted" },
    i18nFilesUploaded: { type: String, default: "file(s) uploaded" },
    i18nUploading: { type: String, default: "Uploading..." },
    i18nUpload: { type: String, default: "Upload" },
    i18nSelectFolderFirst: { type: String, default: "Select a folder first" },
    i18nSelectOneFolder: { type: String, default: "Select at least one folder" },
    i18nNoFilesToExport: { type: String, default: "No files to export" },
    i18nExportQueued: { type: String, default: "Export queued. You'll receive an email when ready." },
    i18nDeleteTitle: { type: String, default: "Delete" },
    // Folder-icon tooltips — builder projects pass "shared with team
    // companies" / "internal — visible to your team" wording instead.
    i18nSharedTitle: { type: String, default: "Shared" },
    i18nPrivateTitle: { type: String, default: "Private" }
  }

  // ── Lifecycle ─────────────────────────────────────────────────

  connect() {
    this._sortableInstances = []
    this._selectedFolderId = null
    this._selectedFolder = null
    this._expandedNodes = new Set()
    this._allExpanded = false
    this._uploadFiles = []
    this._currentFiles = []
    this._renameFileId = null
    this._deleteFileId = null
    this._notifiableMembers = null

    try {
      this._treeData = this.treeDataValue ? JSON.parse(this.treeDataValue) : []
      if (!Array.isArray(this._treeData)) this._treeData = []
    } catch {
      this._treeData = []
    }

    // First load (SLC3-40, SLCC, Phelipe 2026-10-10): the top-level folders open
    // so their subfolders show, but those first subfolders start closed — a deep
    // tree no longer opens all the way down. "Expand all" still opens everything.
    this._treeData.forEach(n => { if (n.children?.length) this._expandedNodes.add(n.id) })
    this._allExpanded = this._treeData.length > 0 &&
      this._collectParentIds(this._treeData).every(id => this._expandedNodes.has(id))

    this._renderTree()
    this._setupPageDrag()

    // Closing the tab mid-upload kills the transfer (the browser does the
    // uploading) — warn first. Multipart files can resume, but the user
    // should still get a chance to let it finish.
    this._uploadsInFlight = false
    this._beforeUnload = (e) => {
      if (!this._uploadsInFlight) return
      e.preventDefault()
      e.returnValue = ""
    }
    window.addEventListener("beforeunload", this._beforeUnload)
  }

  disconnect() {
    this._destroySortables()
    this._teardownPageDrag()
    window.removeEventListener("beforeunload", this._beforeUnload)
  }

  // ── Tree Rendering ────────────────────────────────────────────

  _renderTree() {
    const container = this.treeContainerTarget
    container.innerHTML = ""

    if (this._treeData.length === 0) {
      container.classList.add("hidden")
      this.treeEmptyTarget.classList.remove("hidden")
    } else {
      container.classList.remove("hidden")
      this.treeEmptyTarget.classList.add("hidden")
      this._renderNodes(this._treeData, container, 0)
    }

    if (this.canManageAllValue && !this.sortAlphabeticalValue) {
      this._initAllSortables()
    }
    this._updateToolbarState()
  }

  _renderNodes(nodes, parentEl, depth) {
    nodes.forEach(node => {
      const hasChildren = node.children && node.children.length > 0
      const isExpanded = this._expandedNodes.has(node.id)
      const isSelected = this._selectedFolderId === node.id

      const li = document.createElement("li")
      li.dataset.nodeId = node.id
      li.className = "folder-row"
      const isShared = node.privacy_settings && node.privacy_settings.length > 0 &&
                       !(node.privacy_settings.length === 1 && node.privacy_settings[0] === "private")

      const row = document.createElement("div")
      row.className = `flex items-center gap-1.5 px-2 py-1.5 cursor-pointer select-none group ${
        isSelected ? "bg-blue-50 border-l-2 border-brand" : "border-l-2 border-transparent hover:bg-gray-50"
      }`
      row.style.paddingLeft = `${depth * 20 + 8}px`
      row.dataset.action = "click->documents#selectFolder"
      row.dataset.nodeId = node.id

      // Chevron
      const chevron = document.createElement("div")
      chevron.className = `shrink-0 w-4 h-4 flex items-center justify-center ${hasChildren ? "text-gray-400 hover:text-gray-600" : ""}`
      if (hasChildren) {
        chevron.innerHTML = CHEVRON_SVG
        if (isExpanded) chevron.querySelector("svg").style.transform = "rotate(90deg)"
        chevron.dataset.action = "click->documents#toggleNode"
        chevron.dataset.nodeId = node.id
        chevron.style.cursor = "pointer"
      }
      row.appendChild(chevron)

      // Folder icon (with lock or people overlay)
      const folderIcon = document.createElement("div")
      folderIcon.className = "shrink-0 text-brand"
      folderIcon.title = isShared ? this.i18nSharedTitleValue : this.i18nPrivateTitleValue
      folderIcon.innerHTML = isShared ? FOLDER_SHARED_SVG : FOLDER_PRIVATE_SVG
      row.appendChild(folderIcon)

      // Name
      const name = document.createElement("span")
      name.className = "text-[13px] text-gray-800 truncate"
      name.textContent = node.name
      row.appendChild(name)

      // File count chip (right after name)
      if (node.files_count > 0) {
        const chip = document.createElement("span")
        chip.className = "inline-flex items-center justify-center min-w-[16px] h-[16px] px-0.5 rounded-full text-[11px] font-medium bg-gray-200 text-gray-600 shrink-0 tabular-nums"
        chip.textContent = node.files_count
        row.appendChild(chip)
      }

      // Spacer
      const spacer = document.createElement("div")
      spacer.className = "flex-1"
      row.appendChild(spacer)

      li.appendChild(row)

      // Children container
      if (hasChildren) {
        const childUl = document.createElement("ul")
        childUl.className = `folder-children ${isExpanded ? "" : "hidden"}`
        childUl.dataset.parentId = node.id
        this._renderNodes(node.children, childUl, depth + 1)
        li.appendChild(childUl)
      } else {
        // Empty child container for SortableJS drop target
        const childUl = document.createElement("ul")
        childUl.className = "folder-children hidden"
        childUl.dataset.parentId = node.id
        li.appendChild(childUl)
      }

      parentEl.appendChild(li)
    })
  }

  _nodeMatchesSearch(node, term) {
    if (node.name.toLowerCase().includes(term)) return true
    if (node.children) {
      return node.children.some(child => this._nodeMatchesSearch(child, term))
    }
    return false
  }

  // ── SortableJS (canManageAll only, no drag handle — whole row is draggable) ──

  _initAllSortables() {
    this._destroySortables()
    const rootUl = this.treeContainerTarget
    this._createSortable(rootUl)
    rootUl.querySelectorAll("ul.folder-children").forEach(ul => {
      this._createSortable(ul)
    })
  }

  _createSortable(ul) {
    const instance = Sortable.create(ul, {
      group: "folder-tree",
      animation: 150,
      draggable: ".folder-row",
      fallbackOnBody: true,
      swapThreshold: 0.65,
      emptyInsertThreshold: 8,
      ghostClass: "opacity-30",
      onEnd: () => {
        this._treeData = this._rebuildTreeFromDom()
        this._renderTree()
        this._saveFolderOrder()
      }
    })
    this._sortableInstances.push(instance)
  }

  _destroySortables() {
    (this._sortableInstances || []).forEach(s => s.destroy())
    this._sortableInstances = []
  }

  _rebuildTreeFromDom() {
    return this._readChildrenFromUl(this.treeContainerTarget)
  }

  _readChildrenFromUl(ul) {
    const nodes = []
    ul.querySelectorAll(":scope > li.folder-row").forEach(li => {
      const id = this._parseNodeId(li.dataset.nodeId)
      const original = this._findNodeById(id, this._treeData)
      if (!original) return

      const node = { ...original }
      const childUl = li.querySelector(":scope > ul.folder-children")
      if (childUl) {
        node.children = this._readChildrenFromUl(childUl)
      } else {
        node.children = []
      }
      nodes.push(node)
    })
    return nodes
  }

  async _saveFolderOrder() {
    const token = document.querySelector('meta[name="csrf-token"]')?.content
    try {
      const res = await fetch(this.updateFoldersUrlValue, {
        method: "POST",
        headers: { "Content-Type": "application/json", "X-CSRF-Token": token },
        credentials: "same-origin",
        body: JSON.stringify({ folders: this._flattenForSave(this._treeData) })
      })
      if (!res.ok) throw new Error(`Server responded ${res.status}`)
    } catch (e) {
      console.error("[Documents] Save order failed:", e)
      this._showToast("Failed to save folder order", "error")
    }
  }

  _flattenForSave(nodes) {
    return nodes.filter(n => !n.is_re_stage_folder).map(node => ({
      id: node.id,
      name: node.name,
      privacy_settings: node.privacy_settings,
      children: node.children ? this._flattenForSave(node.children) : []
    }))
  }

  // ── Selection ─────────────────────────────────────────────────

  selectFolder(event) {
    event.stopPropagation()
    const nodeId = this._parseNodeId(event.currentTarget.dataset.nodeId)

    if (this._selectedFolderId === nodeId) return

    this._selectedFolderId = nodeId
    this._selectedFolder = this._findNodeById(nodeId, this._treeData)

    // Expand if has children
    if (this._selectedFolder?.children?.length > 0) {
      this._expandedNodes.add(nodeId)
    }

    this._renderTree()
    this._loadFolderFiles()
  }

  // ── Expand / Collapse ─────────────────────────────────────────

  toggleNode(event) {
    event.stopPropagation()
    const nodeId = this._parseNodeId(event.currentTarget.dataset.nodeId)

    if (this._expandedNodes.has(nodeId)) {
      this._expandedNodes.delete(nodeId)
    } else {
      this._expandedNodes.add(nodeId)
    }

    this._renderTree()
  }

  toggleExpandAll() {
    if (this._allExpanded) {
      this._expandedNodes.clear()
      this._allExpanded = false
    } else {
      this._collectAllIds(this._treeData).forEach(id => this._expandedNodes.add(id))
      this._allExpanded = true
    }
    this._renderTree()
  }

  searchFiles() {
    const term = this.searchInputTarget.value.toLowerCase().trim()
    const rows = this.fileTableBodyTarget.querySelectorAll("tr")
    rows.forEach(row => {
      const name = row.querySelector("td:nth-child(2)")?.textContent?.toLowerCase() || ""
      row.style.display = !term || name.includes(term) ? "" : "none"
    })
  }

  // ── File Loading ──────────────────────────────────────────────

  async _loadFolderFiles() {
    if (!this._selectedFolder) return

    this.noFolderSelectedTarget.classList.add("hidden")
    this.emptyFilesTarget.classList.add("hidden")
    this.fileTableWrapperTarget.classList.add("hidden")
    this.subFoldersTarget.classList.add("hidden")
    this.fileLoadingTarget.classList.remove("hidden")
    this.fileLoadingTarget.style.display = ""

    // Update breadcrumbs
    this._updateBreadcrumbs()

    const token = document.querySelector('meta[name="csrf-token"]')?.content

    try {
      let url, fetchParams
      if (this._selectedFolder.is_re_stage_folder) {
        url = `${this.reStageFolderFilesUrlValue}?stage_id=${this._selectedFolder.stage_id}`
      } else {
        url = `${this.filesUrlValue}?folder_id=${this._selectedFolder.id}`
      }

      const res = await fetch(url, {
        headers: { "Accept": "application/json", "X-CSRF-Token": token },
        credentials: "same-origin"
      })

      if (!res.ok) {
        const errBody = await res.text().catch(() => "")
        throw new Error(`Failed to load files (${res.status}): ${errBody}`)
      }

      this._currentFiles = await res.json()
      this.fileLoadingTarget.classList.add("hidden")

      if (this._currentFiles.length === 0 && (!this._selectedFolder.children || this._selectedFolder.children.length === 0)) {
        this.emptyFilesTarget.classList.remove("hidden")
        this.emptyFilesTarget.style.display = ""
      } else {
        this._renderFileTable()
        this._renderSubFolders()
      }
    } catch (e) {
      console.error("[Documents] Load files failed:", e)
      this.fileLoadingTarget.classList.add("hidden")
      // Still show subfolders even if file loading fails
      this._renderSubFolders()
      this._showToast("Failed to load files", "error")
    }
  }

  _updateBreadcrumbs() {
    if (!this._selectedFolder) return
    const path = this._buildBreadcrumbPath(this._selectedFolder.id, this._treeData)
    this.breadcrumbsTarget.innerHTML = path.map((p, i) => {
      const isLast = i === path.length - 1
      return `<span class="${isLast ? 'text-gray-800 font-medium' : 'text-gray-400 cursor-pointer hover:text-gray-600'}"
                    ${!isLast ? `data-action="click->documents#selectFolder" data-node-id="${p.id}"` : ''}>${this._escapeHtml(p.name)}</span>` +
             (isLast ? '' : '<span class="text-gray-300 mx-1">/</span>')
    }).join("")
  }

  _buildBreadcrumbPath(nodeId, nodes, path = []) {
    for (const node of nodes) {
      if (node.id === nodeId) {
        return [...path, { id: node.id, name: node.name }]
      }
      if (node.children) {
        const found = this._buildBreadcrumbPath(nodeId, node.children, [...path, { id: node.id, name: node.name }])
        if (found) return found
      }
    }
    return null
  }

  _renderFileTable() {
    if (this._currentFiles.length === 0) {
      this.fileTableWrapperTarget.classList.add("hidden")
      return
    }

    this.fileTableWrapperTarget.classList.remove("hidden")
    const tbody = this.fileTableBodyTarget
    tbody.innerHTML = ""

    this._currentFiles.forEach(file => {
      const tr = document.createElement("tr")
      tr.className = "border-b border-gray-50 hover:bg-gray-50 transition-colors cursor-pointer"
      tr.dataset.fileId = file.id
      tr.dataset.fileUrl = file.file_url || ""
      tr.addEventListener("click", (e) => {
        if (e.target.closest("button") || e.target.closest("a")) return
        if (file.is_email && file.email_preview_url) { this._openEmail(file); return }
        if (file.file_url) window.open(file.file_url, "_blank")
      })

      // Email files (.msg/.eml) open the in-app viewer; everything else opens
      // the file directly.
      const nameCell = (file.is_email && file.email_preview_url)
        ? `<button type="button" data-action="click->documents#openEmail" data-file-id="${file.id}"
                   class="text-[13px] text-gray-800 hover:text-brand transition-colors block truncate text-left w-full cursor-pointer"
                   title="${this._escapeHtml(file.filename)}">${this._escapeHtml(file.filename)}</button>`
        : `<a href="${file.file_url || '#'}" target="_blank" rel="noopener"
             class="text-[13px] text-gray-800 hover:text-brand transition-colors block truncate"
             title="${this._escapeHtml(file.filename)}">${this._escapeHtml(file.filename)}</a>`

      tr.innerHTML = `
        <td class="px-2 xl:px-3 py-2 w-8">${this._fileTypeIcon(file.extension)}</td>
        <td class="px-2 xl:px-3 py-2 max-w-0">
          ${nameCell}
          ${this._renderSyncChips(file)}
        </td>
        <td class="px-2 xl:px-3 py-2 text-[13px] text-gray-500 whitespace-nowrap">${this._escapeHtml(file.uploader_name || "")}</td>
        <td class="px-2 xl:px-3 py-2 text-[13px] text-gray-500 whitespace-nowrap">${file.formatted_date || ""}</td>
        <td class="px-2 xl:px-3 py-2 text-[13px] text-gray-500 whitespace-nowrap">${file.size || ""}</td>
        <td class="px-2 xl:px-3 py-2">
          <div class="flex items-center gap-1">
            ${file.can_delete ? `
              <button type="button" class="p-1 rounded text-gray-400 hover:text-gray-600 hover:bg-gray-100 cursor-pointer transition-colors"
                      data-action="click->documents#openRenameFileModal" data-file-id="${file.id}" data-file-name="${this._escapeHtml(file.filename)}"
                      title="Rename">
                <svg class="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor">
                  <path stroke-linecap="round" stroke-linejoin="round" d="m16.862 4.487 1.687-1.688a1.875 1.875 0 1 1 2.652 2.652L10.582 16.07a4.5 4.5 0 0 1-1.897 1.13L6 18l.8-2.685a4.5 4.5 0 0 1 1.13-1.897l8.932-8.931Zm0 0L19.5 7.125M18 14v4.75A2.25 2.25 0 0 1 15.75 21H5.25A2.25 2.25 0 0 1 3 18.75V8.25A2.25 2.25 0 0 1 5.25 6H10" />
                </svg>
              </button>
              <button type="button" class="p-1 rounded text-gray-400 hover:text-red-500 hover:bg-red-50 cursor-pointer transition-colors"
                      data-action="click->documents#openDeleteFileModal" data-file-id="${file.id}" data-file-name="${this._escapeHtml(file.filename)}"
                      title="${this.i18nDeleteTitleValue}">
                <svg class="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor">
                  <path stroke-linecap="round" stroke-linejoin="round" d="m14.74 9-.346 9m-4.788 0L9.26 9m9.968-3.21c.342.052.682.107 1.022.166m-1.022-.165L18.16 19.673a2.25 2.25 0 0 1-2.244 2.077H8.084a2.25 2.25 0 0 1-2.244-2.077L4.772 5.79m14.456 0a48.108 48.108 0 0 0-3.478-.397m-12 .562c.34-.059.68-.114 1.022-.165m0 0a48.11 48.11 0 0 1 3.478-.397m7.5 0v-.916c0-1.18-.91-2.164-2.09-2.201a51.964 51.964 0 0 0-3.32 0c-1.18.037-2.09 1.022-2.09 2.201v.916m7.5 0a48.667 48.667 0 0 0-7.5 0" />
                </svg>
              </button>
            ` : ""}
          </div>
        </td>
      `
      tbody.appendChild(tr)
    })
  }

  _renderSubFolders() {
    const children = this._selectedFolder?.children
    if (!children || children.length === 0) {
      this.subFoldersTarget.classList.add("hidden")
      return
    }

    this.subFoldersTarget.classList.remove("hidden")
    this.subFoldersListTarget.innerHTML = ""

    children.forEach(child => {
      const btn = document.createElement("button")
      btn.type = "button"
      btn.className = "inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-gray-200 bg-white hover:bg-gray-50 text-[13px] text-gray-700 transition-colors cursor-pointer"
      btn.dataset.action = "click->documents#selectFolder"
      btn.dataset.nodeId = child.id

      const isShared = child.privacy_settings && child.privacy_settings.length > 0 &&
                       !(child.privacy_settings.length === 1 && child.privacy_settings[0] === "private")
      btn.title = isShared ? this.i18nSharedTitleValue : this.i18nPrivateTitleValue
      btn.innerHTML = `<span class="text-gray-400">${isShared ? FOLDER_SHARED_SVG : FOLDER_PRIVATE_SVG}</span> ${this._escapeHtml(child.name)}
        ${child.files_count > 0 ? `<span class="text-[11px] text-gray-400">${child.files_count}</span>` : ""}`

      this.subFoldersListTarget.appendChild(btn)
    })
  }

  // ── Toolbar State ─────────────────────────────────────────────

  _updateToolbarState() {
    const hasSelection = this._selectedFolder != null
    const isReStage = this._selectedFolder?.is_re_stage_folder
    const canManage = hasSelection && !isReStage && this._selectedFolder.can_manage === true

    // Edit Folder + delete: always there (greyed until a folder is picked)
    // for canManageAll; for anyone else only while a folder they made is
    // selected. The hidden ATTRIBUTE, not the class — the class loses to
    // inline-flex in this Tailwind build.
    if (this.hasEditFolderBtnTarget) {
      this.editFolderBtnTarget.disabled = !canManage
      this.editFolderBtnTarget.hidden = !this.canManageAllValue && !canManage
    }
    if (this.hasDeleteFolderBtnTarget) {
      this.deleteFolderBtnTarget.disabled = !canManage
      this.deleteFolderBtnTarget.hidden = !this.canManageAllValue && !canManage
    }
    if (this.hasUploadBtnTarget) {
      this.uploadBtnTarget.disabled = !hasSelection || isReStage
    }
    // Phase 2.10 Tier 4 — Folder Activity button enables on any
    // selected folder (re-stage folders are virtual but still carry
    // a stable id for filtering audit logs).
    if (this.hasFolderActivityBtnTarget) {
      this.folderActivityBtnTarget.disabled = !hasSelection
    }
  }

  // Phase 2.10 Tier 4 — opens the Folder Activity slide-over for the
  // currently-selected folder. The slide-over Stimulus controller
  // listens on the window for `folder-activity-drawer:open`.
  openFolderActivity() {
    if (!this._selectedFolder) return
    window.dispatchEvent(new CustomEvent("folder-activity-drawer:open", {
      detail: {
        folderType: "ProjectDocumentsFolder",
        folderId: this._selectedFolder.id,
        title: `Activity — ${this._selectedFolder.name}`,
      },
    }))
  }

  // ── Add Folder ────────────────────────────────────────────────

  openAddFolderModal() {
    this.addFolderNameTarget.value = ""
    if (this.hasAddShareListTarget) this._resetShareList(this.addShareListTarget)
    if (this.hasAddPrivacyConsultantsTarget) this.addPrivacyConsultantsTarget.checked = false
    if (this.hasAddPrivacyContractorsTarget) this.addPrivacyContractorsTarget.checked = false
    if (this.hasAddPrivacyOtherVendorsTarget) this.addPrivacyOtherVendorsTarget.checked = false
    if (this.hasAddPrivacyCollaboratorsTarget) this.addPrivacyCollaboratorsTarget.checked = false
    // Default new folders to be visible to the creator's own role group (e.g. a
    // consultant's folder is shared with Consultants) so their teammates can see it.
    const ownRoleCheckbox = {
      "consultant": this.hasAddPrivacyConsultantsTarget ? this.addPrivacyConsultantsTarget : null,
      "contractor": this.hasAddPrivacyContractorsTarget ? this.addPrivacyContractorsTarget : null,
      "other vendor": this.hasAddPrivacyOtherVendorsTarget ? this.addPrivacyOtherVendorsTarget : null,
      "collaborator": this.hasAddPrivacyCollaboratorsTarget ? this.addPrivacyCollaboratorsTarget : null
    }[this.companyRoleValue]
    if (ownRoleCheckbox) ownRoleCheckbox.checked = true
    // And Consultants in all cases (Phelipe 2026-09-14): an owner's new folder
    // used to come out private, hidden from the consultants who need it.
    if (this.hasAddPrivacyConsultantsTarget) this.addPrivacyConsultantsTarget.checked = true
    this.addFolderModalTarget.classList.remove("hidden")
  }

  // ── Builder share-with-team list (items 15-16) ─────────────────
  // Tokens live on the checkboxes: "company:<id>" shares with the whole
  // company, "user:<id>" with one person. Classic tri-state behavior
  // (Phelipe 2026-07-22): checking the company checks everyone below;
  // picking only some people puts a dash on the company box plus an
  // amber "only X of Y" note so partial selection is obvious.

  _resetShareList(list) {
    list.querySelectorAll("[data-share-token]").forEach(cb => { cb.checked = false; cb.indeterminate = false })
    list.querySelectorAll("[data-share-members]").forEach(el => el.classList.add("hidden"))
    list.querySelectorAll("[data-share-partial-for]").forEach(el => el.classList.add("hidden"))
  }

  _prefillShareList(list, tokens) {
    this._resetShareList(list)
    const set = new Set(tokens || [])
    list.querySelectorAll("[data-share-token]").forEach(cb => {
      const token = cb.dataset.shareToken
      if (set.has(token)) {
        cb.checked = true
        // A person-level grant only makes sense visible — pop its company open.
        if (cb.dataset.shareMemberOf) cb.closest("[data-share-members]")?.classList.remove("hidden")
      }
    })
    list.querySelectorAll("[data-share-company-box]").forEach(companyCb => {
      // Whole-company grant reads as everyone selected.
      if (companyCb.checked) {
        this._shareMembersOf(list, companyCb.dataset.shareCompanyBox).forEach(cb => { cb.checked = true })
      }
      this._syncCompanyShareState(list, companyCb)
    })
  }

  _shareMembersOf(list, companyId) {
    return Array.from(list.querySelectorAll(`[data-share-member-of='${companyId}']`))
  }

  _shareListFor(el) {
    return el.closest("[data-documents-target$='ShareList']") || this.element
  }

  // Company checkbox = select/deselect everyone below.
  shareCompanyChanged(event) {
    const companyCb = event.currentTarget
    const list = this._shareListFor(companyCb)
    this._shareMembersOf(list, companyCb.dataset.shareCompanyBox).forEach(cb => { cb.checked = companyCb.checked })
    this._syncCompanyShareState(list, companyCb)
  }

  // Member checkbox = recompute the company's full / partial / none state.
  shareMemberChanged(event) {
    const memberCb = event.currentTarget
    const list = this._shareListFor(memberCb)
    const companyCb = list.querySelector(`[data-share-company-box='${memberCb.dataset.shareMemberOf}']`)
    if (companyCb) this._syncCompanyShareState(list, companyCb)
  }

  _syncCompanyShareState(list, companyCb) {
    const companyId = companyCb.dataset.shareCompanyBox
    const members = this._shareMembersOf(list, companyId)
    const partialEl = list.querySelector(`[data-share-partial-for='${companyId}']`)

    if (members.length === 0) {
      if (partialEl) partialEl.classList.add("hidden")
      companyCb.indeterminate = false
      return
    }

    const checked = members.filter(cb => cb.checked).length
    companyCb.checked = checked === members.length
    companyCb.indeterminate = checked > 0 && checked < members.length
    if (partialEl) {
      if (companyCb.indeterminate) {
        partialEl.textContent = `only ${checked} of ${members.length}`
        partialEl.classList.remove("hidden")
      } else {
        partialEl.classList.add("hidden")
      }
    }
  }

  toggleShareMembers(event) {
    const row = event.currentTarget.closest(".rounded-lg")
    row?.querySelector("[data-share-members]")?.classList.toggle("hidden")
  }

  closeAddFolderModal() {
    this.addFolderModalTarget.classList.add("hidden")
  }

  // ── Email viewer (.msg / .eml) ──────────────────────────────────

  // data-action entry from the filename button.
  openEmail(event) {
    const id = event.currentTarget.dataset.fileId
    const file = (this._currentFiles || []).find(f => String(f.id) === String(id))
    if (file) this._openEmail(file)
  }

  _openEmail(file) {
    if (!file || !file.email_preview_url) {
      if (file && file.file_url) window.open(file.file_url, "_blank")
      return
    }
    // Reset to the loading state, then open.
    this.emailSubjectTarget.textContent = file.filename || "Email"
    this.emailMetaTarget.innerHTML = ""
    this.emailAttachmentsTarget.innerHTML = ""
    this.emailAttachmentsTarget.classList.add("hidden")
    this.emailAttachmentsTarget.classList.remove("flex")
    this.emailBodyTarget.classList.add("hidden")
    this.emailBodyTarget.removeAttribute("srcdoc")
    this.emailErrorTarget.classList.add("hidden")
    this.emailErrorTarget.classList.remove("flex")
    this.emailLoadingTarget.classList.remove("hidden")
    this.emailDownloadTarget.href = file.file_url || "#"
    this.emailErrorDownloadTarget.href = file.file_url || "#"
    this.emailModalTarget.classList.remove("hidden")

    fetch(file.email_preview_url, { headers: { Accept: "application/json" }, credentials: "same-origin" })
      .then(r => r.json())
      .then(data => data.ok ? this._renderEmail(data) : this._showEmailError())
      .catch(() => this._showEmailError())
  }

  _renderEmail(data) {
    const e = data.email || {}
    this.emailLoadingTarget.classList.add("hidden")
    this.emailSubjectTarget.textContent = e.subject || data.filename || "(no subject)"

    const rows = []
    if (e.from) rows.push(this._emailMetaRow("From", e.from))
    if (e.to) rows.push(this._emailMetaRow("To", e.to))
    if (e.cc) rows.push(this._emailMetaRow("Cc", e.cc))
    if (e.date) rows.push(this._emailMetaRow("Date", e.date))
    this.emailMetaTarget.innerHTML = rows.join("")

    const atts = (e.attachments || []).filter(a => a && a.name)
    if (atts.length) {
      this.emailAttachmentsTarget.innerHTML =
        `<span class="text-gray-400 mr-1">Attachments:</span>` +
        atts.map(a => `<span class="inline-flex items-center gap-1 px-2 py-0.5 rounded bg-gray-100 text-gray-600">${this._escapeHtml(a.name)}${a.size ? ` <span class="text-gray-400">(${this._fmtEmailSize(a.size)})</span>` : ""}</span>`).join("")
      this.emailAttachmentsTarget.classList.remove("hidden")
      this.emailAttachmentsTarget.classList.add("flex")
    }

    // Render the sanitized body in the script-disabled sandboxed iframe.
    const body = e.body_html || "<p style='color:#888'>(empty message)</p>"
    // Emails define their own (light-assuming) colors, so render them on white
    // paper regardless of the app's theme — otherwise dark body text vanishes on
    // the dark viewer in dark mode. The iframe is a separate document, so the
    // app's data-theme="dark" CSS never reaches inside it.
    this.emailBodyTarget.srcdoc =
      `<!doctype html><html><head><meta charset="utf-8"><base target="_blank">` +
      `<style>html,body{background:#ffffff}body{font-family:-apple-system,BlinkMacSystemFont,'Segoe UI',Roboto,sans-serif;font-size:13px;line-height:1.5;color:#111827;margin:16px;word-break:break-word}` +
      `img{max-width:100%;height:auto}table{max-width:100%}a{color:#2563eb}</style></head><body>${body}</body></html>`
    this.emailBodyTarget.classList.remove("hidden")
  }

  _emailMetaRow(label, value) {
    return `<div class="flex gap-2"><span class="text-gray-400 shrink-0 w-9">${label}</span><span class="text-gray-700 min-w-0 break-words">${this._escapeHtml(value)}</span></div>`
  }

  _fmtEmailSize(b) {
    if (b < 1024) return `${b} B`
    if (b < 1048576) return `${Math.round(b / 1024)} KB`
    return `${(b / 1048576).toFixed(1)} MB`
  }

  _showEmailError() {
    this.emailLoadingTarget.classList.add("hidden")
    this.emailBodyTarget.classList.add("hidden")
    this.emailErrorTarget.classList.remove("hidden")
    this.emailErrorTarget.classList.add("flex")
  }

  closeEmailModal() {
    this.emailModalTarget.classList.add("hidden")
    this.emailBodyTarget.removeAttribute("srcdoc")
  }

  async submitAddFolder() {
    const name = this.addFolderNameTarget.value.trim()
    if (!name) return

    const privacySettings = this._collectPrivacySettings("add")
    const token = document.querySelector('meta[name="csrf-token"]')?.content

    try {
      const res = await fetch(this.newFolderUrlValue, {
        method: "POST",
        headers: { "Content-Type": "application/json", "X-CSRF-Token": token },
        credentials: "same-origin",
        body: JSON.stringify({
          name,
          parent_id: this._selectedFolderId,
          privacy_settings: privacySettings
        })
      })

      if (!res.ok) {
        const err = await res.json()
        throw new Error(err.error || "Failed to create folder")
      }

      const folder = await res.json()
      folder.is_re_stage_folder = false

      // Add to tree
      if (this._selectedFolderId) {
        const parent = this._findNodeById(this._selectedFolderId, this._treeData)
        if (parent) {
          if (!parent.children) parent.children = []
          parent.children.push(folder)
          this._expandedNodes.add(this._selectedFolderId)
        }
      } else {
        this._treeData.push(folder)
      }

      this.closeAddFolderModal()
      this._renderTree()
      this._showToast(this.i18nFolderCreatedValue)
    } catch (e) {
      console.error("[Documents] Create folder failed:", e)
      this._showToast(e.message || "Failed to create folder", "error")
    }
  }

  // ── Edit Folder ───────────────────────────────────────────────

  openEditFolderModal() {
    if (!this._selectedFolder?.can_manage || !this.hasEditFolderModalTarget) return

    this.editFolderNameTarget.value = this._selectedFolder.name
    const ps = this._selectedFolder.privacy_settings || []
    if (this.hasEditShareListTarget) this._prefillShareList(this.editShareListTarget, ps)
    if (this.hasEditPrivacyConsultantsTarget) this.editPrivacyConsultantsTarget.checked = ps.includes("consultants")
    if (this.hasEditPrivacyContractorsTarget) this.editPrivacyContractorsTarget.checked = ps.includes("contractors")
    if (this.hasEditPrivacyOtherVendorsTarget) this.editPrivacyOtherVendorsTarget.checked = ps.includes("other_vendors")
    if (this.hasEditPrivacyCollaboratorsTarget) this.editPrivacyCollaboratorsTarget.checked = ps.includes("collaborators")
    this.editFolderModalTarget.classList.remove("hidden")
  }

  closeEditFolderModal() {
    if (!this.hasEditFolderModalTarget) return
    this.editFolderModalTarget.classList.add("hidden")
  }

  async submitEditFolder() {
    if (!this._selectedFolder) return
    const name = this.editFolderNameTarget.value.trim()
    if (!name) return

    const privacySettings = this._collectPrivacySettings("edit")
    const token = document.querySelector('meta[name="csrf-token"]')?.content

    try {
      const res = await fetch(this.updateFolderNameUrlValue, {
        method: "PUT",
        headers: { "Content-Type": "application/json", "X-CSRF-Token": token },
        credentials: "same-origin",
        body: JSON.stringify({
          folder_id: this._selectedFolder.id,
          name,
          privacy_settings: privacySettings
        })
      })

      if (!res.ok) throw new Error("Failed to update folder")

      const data = await res.json()
      this._selectedFolder.name = data.name
      this._selectedFolder.privacy_settings = data.privacy_settings

      this.closeEditFolderModal()
      this._renderTree()
      this._showToast(this.i18nFolderUpdatedValue)
    } catch (e) {
      console.error("[Documents] Update folder failed:", e)
      this._showToast(e.message || "Failed to update folder", "error")
    }
  }

  // ── Delete Folder ─────────────────────────────────────────────

  openDeleteFolderModal() {
    if (!this._selectedFolder?.can_manage) return
    this.deleteFolderMessageTarget.textContent = `Are you sure you want to delete "${this._selectedFolder.name}"? This action cannot be undone.`
    this.deleteFolderModalTarget.classList.remove("hidden")
  }

  closeDeleteFolderModal() {
    this.deleteFolderModalTarget.classList.add("hidden")
  }

  async confirmDeleteFolder() {
    if (!this._selectedFolder) return
    const token = document.querySelector('meta[name="csrf-token"]')?.content

    try {
      const res = await fetch(this.removeFolderUrlValue, {
        method: "DELETE",
        headers: { "Content-Type": "application/json", "X-CSRF-Token": token },
        credentials: "same-origin",
        body: JSON.stringify({ folder_id: this._selectedFolder.id })
      })

      if (!res.ok) throw new Error("Failed to delete folder")

      // Remove from tree
      this._removeNodeById(this._selectedFolder.id, this._treeData)
      this._selectedFolderId = null
      this._selectedFolder = null

      this.closeDeleteFolderModal()
      this._renderTree()
      this._showFilePanel("noFolder")
      this._showToast(this.i18nFolderDeletedValue)
    } catch (e) {
      console.error("[Documents] Delete folder failed:", e)
      this._showToast(e.message || "Failed to delete folder", "error")
    }
  }

  // ── Upload ────────────────────────────────────────────────────

  openUploadModal() {
    if (!this._selectedFolder || this._selectedFolder.is_re_stage_folder) return

    // Housekeeping: expire resume records whose S3 uploads are long gone.
    const csrf = document.querySelector('meta[name="csrf-token"]')?.content
    this._purgeStaleMultipart(csrf)

    this._uploadFiles = []
    this.uploadFileListTarget.innerHTML = ""
    this.uploadFileListTarget.classList.add("hidden")
    this.uploadSubmitBtnTarget.disabled = true
    this.uploadFolderNameTarget.textContent = this._selectedFolder.name

    // Reset notify
    this._notifiableMembers = null
    if (this.hasNotifyToggleTarget) this.notifyToggleTarget.checked = false
    if (this.hasNotifyMembersListTarget) {
      this.notifyMembersListTarget.classList.add("hidden")
      this.notifyMembersListTarget.innerHTML = ""
    }
    if (this.hasNotifySectionTarget) this.notifySectionTarget.classList.remove("hidden")

    this.uploadModalTarget.classList.remove("hidden")
  }

  closeUploadModal() {
    this.uploadModalTarget.classList.add("hidden")
    this._uploadFiles = []
  }

  triggerFileInput() {
    this.fileInputTarget.click()
  }

  // Whole-folder upload (item 17): the webkitdirectory picker delivers every
  // file with a webkitRelativePath; the subfolder structure is recreated
  // inside the selected folder at upload time.
  triggerFolderInput() {
    if (this.hasFolderInputTarget) this.folderInputTarget.click()
  }

  handleFolderFiles(event) {
    this._addUploadFiles(event.target?.files || [])
    if (event.target) event.target.value = ""
  }

  handleFiles(event) {
    const files = event.target?.files || event
    this._addUploadFiles(files)
    if (event.target) event.target.value = ""
  }

  _addUploadFiles(fileList) {
    if (!fileList || fileList.length === 0) return

    for (const file of fileList) {
      // Hidden junk that rides along inside dropped/picked folders.
      if (file.name.startsWith(".")) continue
      const ext = file.name.split(".").pop()?.toLowerCase()
      if (!ALLOWED_EXTENSIONS.includes(ext)) {
        this._showToast(`File type .${ext} is not allowed`, "error")
        continue
      }
      // Videos + Insta360 files may run huge (multipart path, 15GB);
      // everything else keeps the regular caps.
      const sizeLimit = MULTIPART_EXTENSIONS.includes(ext) ? MULTIPART_MAX_UPLOAD_SIZE
                      : (VIDEO_EXTENSIONS.includes(ext) ? VIDEO_MAX_UPLOAD_SIZE : MAX_UPLOAD_SIZE)
      if (file.size > sizeLimit) {
        const human = sizeLimit >= 1024 ** 3 ? `${Math.round(sizeLimit / 1024 ** 3)}GB` : `${Math.round(sizeLimit / 1048576)}MB`
        this._showToast(`${file.name} exceeds ${human} limit`, "error")
        continue
      }
      // "Folder/sub/file.pdf" -> relDir "Folder/sub" (empty for loose files).
      const rel = file._relPath || file.webkitRelativePath || ""
      const relDir = rel.includes("/") ? rel.slice(0, rel.lastIndexOf("/")) : ""
      const id = Date.now() + Math.round(Math.random() * 1000)
      this._uploadFiles.push({ id, file, relDir })
      this._renderUploadFileRow(id, file, relDir)
    }

    if (this._uploadFiles.length > 0) {
      this.uploadFileListTarget.classList.remove("hidden")
    }
    this.uploadSubmitBtnTarget.disabled = this._uploadFiles.length === 0
  }

  _renderUploadFileRow(id, file, relDir = "") {
    const row = document.createElement("div")
    row.dataset.uploadRow = id
    row.className = "flex items-center gap-3 px-3 py-1.5 bg-gray-50 rounded-lg"

    const shownName = relDir ? `${relDir}/${file.name}` : file.name
    row.innerHTML = `
      <div class="flex-1 min-w-0">
        <div class="flex items-center gap-2">
          <p class="text-[13px] text-gray-700 truncate" title="${this._escapeHtml(shownName)}">${this._escapeHtml(shownName)}</p>
          <span class="text-[11px] text-gray-400 shrink-0">${this._formatFileSize(file.size)}</span>
        </div>
        <div class="w-full bg-gray-200 rounded-full h-1 mt-1.5 hidden" data-progress-track>
          <div class="bg-brand h-1 rounded-full transition-all" data-progress-bar style="width: 0%"></div>
        </div>
      </div>
      <button type="button" class="shrink-0 p-1 text-gray-400 hover:text-red-500 cursor-pointer transition-colors"
              data-action="click->documents#removeUploadFile" data-upload-id="${id}">
        <svg class="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor">
          <path stroke-linecap="round" stroke-linejoin="round" d="M6 18 18 6M6 6l12 12"/>
        </svg>
      </button>
    `

    this.uploadFileListTarget.appendChild(row)
  }

  removeUploadFile(event) {
    const id = parseInt(event.currentTarget.dataset.uploadId)
    this._uploadFiles = this._uploadFiles.filter(f => f.id !== id)
    const row = this.uploadFileListTarget.querySelector(`[data-upload-row="${id}"]`)
    if (row) row.remove()
    this.uploadSubmitBtnTarget.disabled = this._uploadFiles.length === 0
    if (this._uploadFiles.length === 0) {
      this.uploadFileListTarget.classList.add("hidden")
    }
  }

  dragOverZone(event) {
    event.preventDefault()
    event.currentTarget.classList.add("border-brand", "bg-brand/5")
  }

  dragLeaveZone(event) {
    event.currentTarget.classList.remove("border-brand", "bg-brand/5")
  }

  async dropOnZone(event) {
    event.preventDefault()
    event.currentTarget.classList.remove("border-brand", "bg-brand/5")
    // Dropped a folder? Walk it (item 17) — otherwise the plain file list.
    const traversed = await this._filesFromDataTransfer(event.dataTransfer)
    this._addUploadFiles(traversed || event.dataTransfer.files)
  }

  // Returns a flat File array (with _relPath set) when the drop contains at
  // least one directory; null for plain-file drops so the caller keeps the
  // original FileList. Entries must be grabbed synchronously — they're gone
  // after the first await.
  async _filesFromDataTransfer(dataTransfer) {
    const entries = []
    for (const item of dataTransfer.items || []) {
      const entry = item.webkitGetAsEntry && item.webkitGetAsEntry()
      if (entry) entries.push(entry)
    }
    if (!entries.some(e => e && e.isDirectory)) return null

    const files = []
    const walk = async (entry, path) => {
      if (entry.isFile) {
        const file = await new Promise((resolve, reject) => entry.file(resolve, reject))
        file._relPath = path ? `${path}/${file.name}` : file.name
        files.push(file)
      } else if (entry.isDirectory) {
        const dirPath = path ? `${path}/${entry.name}` : entry.name
        const reader = entry.createReader()
        // readEntries hands back at most ~100 entries per call — drain it.
        let batch
        do {
          batch = await new Promise((resolve, reject) => reader.readEntries(resolve, reject))
          for (const child of batch) await walk(child, dirPath)
        } while (batch.length > 0)
      }
    }
    for (const entry of entries) await walk(entry, "")
    return files
  }

  async toggleNotify() {
    const checked = this.notifyToggleTarget.checked
    if (!checked) {
      this.notifyMembersListTarget.classList.add("hidden")
      return
    }

    this.notifyMembersListTarget.classList.remove("hidden")

    if (!this._notifiableMembers) {
      const token = document.querySelector('meta[name="csrf-token"]')?.content
      try {
        const res = await fetch(this.notifiableMembersUrlValue, {
          headers: { "Accept": "application/json", "X-CSRF-Token": token },
          credentials: "same-origin"
        })
        if (!res.ok) throw new Error(`Server responded ${res.status}`)
        this._notifiableMembers = await res.json()
      } catch (e) {
        console.error("[Documents] Load members failed:", e)
        return
      }
    }

    this._renderNotifyMembers()
  }

  _renderNotifyMembers() {
    this.notifyMembersListTarget.innerHTML = ""
    if (!this._notifiableMembers) return

    this._notifiableMembers.forEach(group => {
      const section = document.createElement("div")
      section.innerHTML = `<p class="text-[13px] font-semibold text-gray-500 mb-1">${this._escapeHtml(group.company)}</p>`

      group.members.forEach(member => {
        const locked = !!member.documents_notif
        const label = document.createElement("label")
        label.className = `flex items-center gap-2 py-0.5 ${locked ? "cursor-default opacity-70" : "cursor-pointer"}`
        label.innerHTML = `
          <input type="checkbox" class="form-checkbox rounded notify-member-cb" value="${this._escapeHtml(member.email)}"
                 style="accent-color: var(--color-brand)" ${locked ? "checked disabled" : ""}>
          <span class="text-[13px] text-gray-700">${this._escapeHtml(member.name)}</span>
        `
        section.appendChild(label)
      })

      this.notifyMembersListTarget.appendChild(section)
    })
  }

  async startUpload() {
    if (this._uploadFiles.length === 0) return
    this._uploadsInFlight = true
    this.uploadSubmitBtnTarget.disabled = true
    this.uploadSubmitBtnTarget.textContent = this.i18nUploadingValue

    const token = document.querySelector('meta[name="csrf-token"]')?.content

    try {
      // 0. Whole-folder uploads (item 17): recreate the dropped/picked
      // folder structure inside the selected folder before anything moves.
      const dirIds = await this._ensureUploadFolders(token)

      // Big videos / Insta360 files take the S3 multipart lane (a single
      // presigned PUT caps at 5GB); everything else keeps the classic
      // presign → PUT → register flow.
      const isBig = (uf) => {
        const ext = uf.file.name.split(".").pop()?.toLowerCase()
        if (!MULTIPART_EXTENSIONS.includes(ext)) return false
        const singleCap = VIDEO_EXTENSIONS.includes(ext) ? VIDEO_MAX_UPLOAD_SIZE : MAX_UPLOAD_SIZE
        return uf.file.size > singleCap
      }
      const smallFiles = this._uploadFiles.filter(uf => !isBig(uf))
      const bigFiles = this._uploadFiles.filter(isBig)

      // 1. Presign
      const fileMeta = smallFiles.map(uf => ({
        filename: uf.file.name,
        content_type: uf.file.type || "application/octet-stream",
        byte_size: uf.file.size
      }))

      const presignRes = await fetch(this.presignUrlValue, {
        method: "POST",
        headers: { "Content-Type": "application/json", "X-CSRF-Token": token },
        credentials: "same-origin",
        body: JSON.stringify({ files: fileMeta })
      })

      if (!presignRes.ok) {
        const err = await presignRes.json()
        throw new Error(err.error || "Presign failed")
      }

      const { uploads } = await presignRes.json()

      // 2. PUT to S3 with progress
      const uploadPromises = uploads.map((upload, i) => {
        return new Promise((resolve, reject) => {
          const xhr = new XMLHttpRequest()
          const uf = smallFiles[i]

          const progressTrack = this.uploadFileListTarget.querySelector(`[data-upload-row="${uf.id}"] [data-progress-track]`)
          const progressBar = this.uploadFileListTarget.querySelector(`[data-upload-row="${uf.id}"] [data-progress-bar]`)
          if (progressTrack) { progressTrack.classList.remove("hidden") }

          xhr.upload.onprogress = (e) => {
            if (e.lengthComputable && progressBar) {
              const pct = Math.round((e.loaded / e.total) * 100)
              progressBar.style.width = `${pct}%`
            }
          }

          xhr.onload = () => {
            if (xhr.status >= 200 && xhr.status < 300) {
              resolve({ cache_name: upload.cache_name, filename: upload.filename })
            } else {
              reject(new Error(`Upload failed: ${xhr.status}`))
            }
          }
          xhr.onerror = () => reject(new Error("Upload error"))

          xhr.open("PUT", upload.upload_url)
          xhr.send(uf.file)
        })
      })

      const results = await Promise.all(uploadPromises)

      // 3. Register — grouped per destination folder, since folder uploads
      // land each file in its own recreated subfolder.
      const byFolder = new Map()
      results.forEach((r, i) => {
        const relDir = smallFiles[i]?.relDir || ""
        const fid = dirIds.get(relDir) ?? this._selectedFolder.id
        if (!byFolder.has(fid)) byFolder.set(fid, [])
        byFolder.get(fid).push(r)
      })

      let registered = []
      for (const [fid, files] of byFolder) {
        const registerRes = await fetch(this.registerUrlValue, {
          method: "POST",
          headers: { "Content-Type": "application/json", "X-CSRF-Token": token },
          credentials: "same-origin",
          body: JSON.stringify({ files, folder_id: fid })
        })
        if (!registerRes.ok) throw new Error("Register failed")
        registered = registered.concat(await registerRes.json())
        this._updateFolderCount(fid, files.length)
      }

      // 3b. Multipart lane — one big file at a time, ~100MB parts, same
      // progress bars. Each completes server-side and needs no register step
      // (the attachment is created hidden at presign and revealed on finish).
      for (const uf of bigFiles) {
        const fid = dirIds.get(uf.relDir || "") ?? this._selectedFolder.id
        const done = await this._uploadMultipart(uf, token, fid)
        registered.push(done)
        results.push(done)
        this._updateFolderCount(fid, 1)
      }

      // 4. Send notifications if checked
      if (this.hasNotifyToggleTarget && this.notifyToggleTarget.checked) {
        const checkedBoxes = this.notifyMembersListTarget.querySelectorAll(".notify-member-cb:checked")
        const emails = Array.from(checkedBoxes).map(cb => cb.value)

        if (emails.length > 0) {
          const attachmentIds = registered.map(a => a.id)
          await fetch(this.saveFolderFilesUrlValue, {
            method: "POST",
            headers: { "Content-Type": "application/json", "X-CSRF-Token": token },
            credentials: "same-origin",
            body: JSON.stringify({
              folder_id: this._selectedFolder.id,
              attachment_ids: attachmentIds,
              emails_to_notify: emails
            })
          })
        }
      }

      this.closeUploadModal()
      this._showToast(`${results.length} ${this.i18nFilesUploadedValue}`)

      // Refresh file list and re-render the tree (per-folder counts were
      // bumped during registration)
      this._loadFolderFiles()
      this._renderTree()
    } catch (e) {
      console.error("[Documents] Upload failed:", e)
      this._showToast(e.message || "Upload failed", "error")
    } finally {
      this._uploadsInFlight = false
      this.uploadSubmitBtnTarget.disabled = false
      this.uploadSubmitBtnTarget.textContent = this.i18nUploadValue
    }
  }

  // Whole-folder uploads (item 17): make sure every relative directory in the
  // pending list exists as a real subfolder (created parents-first, privacy
  // inherited from the destination folder; an existing same-named subfolder
  // is reused). Returns Map relDir -> folder id ("" -> the selected folder).
  // S3 multipart upload for one big video / Insta360 file: presign (creates
  // the attachment hidden and mints part URLs), PUT each ~100MB slice with
  // aggregate progress on the file's row, then complete (server verifies all
  // parts arrived and reveals the attachment).
  //
  // RESUMABLE: the session (attachment/upload ids + part size) is remembered
  // per file identity (name+size+mtime) in localStorage. If the laptop closes
  // or the connection dies mid-upload, re-adding the SAME file continues from
  // the parts already on S3 instead of starting the 6GB over. That's also why
  // a failed part does NOT abort the upload server-side — aborting would
  // throw away the resumable progress.
  async _uploadMultipart(uf, token, folderId) {
    const storeKey = this._mpuKey(uf)

    // Try to resume a previous attempt for this exact file first.
    let session = null
    const saved = this._readMpuRecord(storeKey)
    if (saved) session = await this._resumeMultipartSession(saved, uf, token, storeKey)

    if (!session) {
      const presignRes = await fetch(this.presignMultipartUrlValue, {
        method: "POST",
        headers: { "Content-Type": "application/json", "X-CSRF-Token": token },
        credentials: "same-origin",
        body: JSON.stringify({
          filename: uf.file.name,
          content_type: uf.file.type || "application/octet-stream",
          byte_size: uf.file.size,
          folder_id: folderId
        })
      })
      if (!presignRes.ok) {
        const err = await presignRes.json().catch(() => ({}))
        throw new Error(err.error || "Presign failed")
      }
      const fresh = await presignRes.json()
      session = { ...fresh, uploaded_parts: [] }
      try {
        localStorage.setItem(storeKey, JSON.stringify({
          attachmentId: fresh.attachment_id, uploadId: fresh.upload_id,
          partSize: fresh.part_size, t: Date.now()
        }))
      } catch (_) { /* storage full/blocked — uploads still work, just not resumable */ }
    }

    const { attachment_id, upload_id, part_size, part_urls } = session
    const uploadedSet = new Set(session.uploaded_parts || [])
    const lastPart = part_urls.length
    const partBytes = (n) => n < lastPart ? part_size : (uf.file.size - part_size * (lastPart - 1))

    const progressTrack = this.uploadFileListTarget.querySelector(`[data-upload-row="${uf.id}"] [data-progress-track]`)
    const progressBar = this.uploadFileListTarget.querySelector(`[data-upload-row="${uf.id}"] [data-progress-bar]`)
    if (progressTrack) progressTrack.classList.remove("hidden")

    let uploadedBytes = [...uploadedSet].reduce((sum, n) => sum + partBytes(n), 0)
    if (uploadedSet.size > 0) {
      const pct = Math.round((uploadedBytes / uf.file.size) * 100)
      if (progressBar) progressBar.style.width = `${pct}%`
      this._showToast(`Resuming ${uf.file.name} from ${pct}%`)
    }

    try {
      for (const part of part_urls) {
        if (uploadedSet.has(part.part_number)) continue
        const start = (part.part_number - 1) * part_size
        const blob = uf.file.slice(start, Math.min(start + part_size, uf.file.size))
        await new Promise((resolve, reject) => {
          const xhr = new XMLHttpRequest()
          xhr.upload.onprogress = (e) => {
            if (e.lengthComputable && progressBar) {
              const pct = Math.round(((uploadedBytes + e.loaded) / uf.file.size) * 100)
              progressBar.style.width = `${pct}%`
            }
          }
          xhr.onload = () => {
            if (xhr.status >= 200 && xhr.status < 300) resolve()
            else reject(new Error(`Upload failed on part ${part.part_number} of ${uf.file.name}`))
          }
          xhr.onerror = () => reject(new Error(`Connection lost uploading ${uf.file.name}`))
          xhr.open("PUT", part.url)
          xhr.send(blob)
        })
        uploadedBytes += blob.size
      }

      const completeRes = await fetch(this.completeMultipartUrlValue, {
        method: "POST",
        headers: { "Content-Type": "application/json", "X-CSRF-Token": token },
        credentials: "same-origin",
        body: JSON.stringify({ attachment_id, upload_id, expected_parts: part_urls.length })
      })
      if (!completeRes.ok) {
        const err = await completeRes.json().catch(() => ({}))
        throw new Error(err.error || "Couldn't finish the upload")
      }
      try { localStorage.removeItem(storeKey) } catch (_) { /* fine */ }
      return await completeRes.json()
    } catch (e) {
      // Keep the session — the parts already uploaded stay on S3 for resume.
      e.message = `${e.message} — your progress is saved; add the same file again to resume.`
      throw e
    }
  }

  // File identity for resume: same name + size + last-modified = same file.
  _mpuKey(uf) {
    return `conslog.mpu.${this.projectIdValue}.${uf.file.name}|${uf.file.size}|${uf.file.lastModified}`
  }

  _readMpuRecord(key) {
    try {
      const rec = JSON.parse(localStorage.getItem(key) || "null")
      return rec && rec.attachmentId && rec.uploadId && rec.partSize ? rec : null
    } catch (_) { return null }
  }

  // Ask the server which parts already arrived + fresh part URLs (the
  // originals expire). Any failure clears the record and returns null so the
  // caller starts a fresh upload.
  async _resumeMultipartSession(rec, uf, token, storeKey) {
    try {
      const res = await fetch(this.resumeMultipartUrlValue, {
        method: "POST",
        headers: { "Content-Type": "application/json", "X-CSRF-Token": token },
        credentials: "same-origin",
        body: JSON.stringify({ attachment_id: rec.attachmentId, upload_id: rec.uploadId,
                               part_size: rec.partSize, byte_size: uf.file.size })
      })
      if (!res.ok) { try { localStorage.removeItem(storeKey) } catch (_) { /* fine */ } ; return null }
      return await res.json()
    } catch (_) { return null }
  }

  // Resume records older than ~6 days point at uploads S3 will have expired —
  // tell the server to abort them (which also removes the hidden placeholder
  // attachment) and forget them locally. Fire-and-forget on modal open.
  _purgeStaleMultipart(token) {
    const cutoff = Date.now() - 6 * 24 * 3600 * 1000
    const prefix = `conslog.mpu.${this.projectIdValue}.`
    for (let i = localStorage.length - 1; i >= 0; i--) {
      const key = localStorage.key(i)
      if (!key || !key.startsWith(prefix)) continue
      let rec = null
      try { rec = JSON.parse(localStorage.getItem(key)) } catch (_) { rec = null }
      if (rec && rec.t && rec.t >= cutoff) continue
      if (rec && rec.attachmentId) {
        fetch(this.abortMultipartUrlValue, {
          method: "POST",
          headers: { "Content-Type": "application/json", "X-CSRF-Token": token },
          credentials: "same-origin",
          body: JSON.stringify({ attachment_id: rec.attachmentId, upload_id: rec.uploadId })
        }).catch(() => {})
      }
      try { localStorage.removeItem(key) } catch (_) { /* fine */ }
    }
  }

  async _ensureUploadFolders(token) {
    const map = new Map([["", this._selectedFolder.id]])

    const dirs = new Set()
    this._uploadFiles.forEach(uf => {
      if (!uf.relDir) return
      const parts = uf.relDir.split("/")
      for (let i = 1; i <= parts.length; i++) dirs.add(parts.slice(0, i).join("/"))
    })
    if (dirs.size === 0) return map

    const sorted = Array.from(dirs).sort((a, b) => a.split("/").length - b.split("/").length)
    for (const dir of sorted) {
      const parentPath = dir.includes("/") ? dir.slice(0, dir.lastIndexOf("/")) : ""
      const name = dir.split("/").pop()
      const parentId = map.get(parentPath)
      const parentNode = this._findNodeById(parentId, this._treeData)

      const existing = parentNode?.children?.find(ch =>
        !ch.is_re_stage_folder && (ch.name || "").toLowerCase() === name.toLowerCase())
      if (existing) { map.set(dir, existing.id); continue }

      const res = await fetch(this.newFolderUrlValue, {
        method: "POST",
        headers: { "Content-Type": "application/json", "X-CSRF-Token": token },
        credentials: "same-origin",
        body: JSON.stringify({
          name,
          parent_id: parentId,
          privacy_settings: this._selectedFolder.privacy_settings
        })
      })
      if (!res.ok) {
        const err = await res.json().catch(() => ({}))
        throw new Error(err.error || `Could not create folder "${name}"`)
      }
      const folder = await res.json()
      folder.is_re_stage_folder = false
      map.set(dir, folder.id)
      if (parentNode) {
        if (!parentNode.children) parentNode.children = []
        parentNode.children.push(folder)
        this._expandedNodes.add(parentNode.id)
      }
    }
    return map
  }

  // ── Rename File ───────────────────────────────────────────────

  openRenameFileModal(event) {
    event.stopPropagation()
    this._renameFileId = event.currentTarget.dataset.fileId
    this.renameFileInputTarget.value = event.currentTarget.dataset.fileName || ""
    this.renameFileModalTarget.classList.remove("hidden")
  }

  closeRenameFileModal() {
    this.renameFileModalTarget.classList.add("hidden")
    this._renameFileId = null
  }

  async submitRenameFile() {
    if (!this._renameFileId) return
    const newName = this.renameFileInputTarget.value.trim()
    if (!newName) return

    const token = document.querySelector('meta[name="csrf-token"]')?.content

    try {
      const res = await fetch(this.updateFileUrlValue, {
        method: "POST",
        headers: { "Content-Type": "application/json", "X-CSRF-Token": token },
        credentials: "same-origin",
        body: JSON.stringify({ file_id: this._renameFileId, file_name: newName })
      })

      if (!res.ok) throw new Error("Failed to rename file")

      this.closeRenameFileModal()
      this._showToast(this.i18nFileRenamedValue)
      this._loadFolderFiles()
    } catch (e) {
      console.error("[Documents] Rename failed:", e)
      this._showToast(e.message || "Failed to rename file", "error")
    }
  }

  // ── Delete File ───────────────────────────────────────────────

  openDeleteFileModal(event) {
    event.stopPropagation()
    this._deleteFileId = event.currentTarget.dataset.fileId
    const fileName = event.currentTarget.dataset.fileName || "this file"
    this.deleteFileMessageTarget.textContent = `Are you sure you want to delete "${fileName}"?`
    this.deleteFileModalTarget.classList.remove("hidden")
  }

  closeDeleteFileModal() {
    this.deleteFileModalTarget.classList.add("hidden")
    this._deleteFileId = null
  }

  async confirmDeleteFile() {
    if (!this._deleteFileId) return
    const token = document.querySelector('meta[name="csrf-token"]')?.content

    try {
      const res = await fetch(this.destroyAttachmentUrlValue, {
        method: "DELETE",
        headers: { "Content-Type": "application/json", "X-CSRF-Token": token },
        credentials: "same-origin",
        body: JSON.stringify({ attachment_id: this._deleteFileId })
      })

      if (!res.ok) throw new Error("Failed to delete file")

      // Remove the row from the table
      const row = this.fileTableBodyTarget.querySelector(`tr[data-file-id="${this._deleteFileId}"]`)
      if (row) row.remove()

      // Remove from in-memory list
      this._currentFiles = this._currentFiles.filter(f => String(f.id) !== String(this._deleteFileId))

      // Show empty state if no files left
      if (this._currentFiles.length === 0) {
        this.fileTableWrapperTarget.classList.add("hidden")
        if (!this._selectedFolder?.children?.length) {
          this.emptyFilesTarget.classList.remove("hidden")
          this.emptyFilesTarget.style.display = ""
        }
      }

      // Update tree counts
      this._updateFolderCount(this._selectedFolder.id, -1)
      this._renderTree()

      this.closeDeleteFileModal()
      this._showToast(this.i18nFileDeletedValue)
    } catch (e) {
      console.error("[Documents] Delete file failed:", e)
      this._showToast(e.message || "Failed to delete file", "error")
    }
  }

  // ── Export ────────────────────────────────────────────────────

  openExportModal() {
    this._renderExportTree()
    this.exportModalTarget.classList.remove("hidden")
  }

  closeExportModal() {
    this.exportModalTarget.classList.add("hidden")
  }

  _renderExportTree() {
    this.exportTreeTarget.innerHTML = ""
    this._renderExportNodes(this._treeData, this.exportTreeTarget, 0)
  }

  _renderExportNodes(nodes, container, depth) {
    nodes.forEach(node => {
      if (node.is_re_stage_folder) return

      const div = document.createElement("div")
      div.style.paddingLeft = `${depth * 20}px`
      div.className = "flex items-center gap-2 py-1"

      div.innerHTML = `
        <label class="flex items-center gap-2 cursor-pointer">
          <input type="checkbox" class="form-checkbox rounded export-folder-cb" value="${node.id}"
                 data-action="change->documents#toggleExportFolder"
                 style="accent-color: var(--color-brand)" checked>
          <span class="text-[13px] text-gray-700">${this._escapeHtml(node.name)}</span>
          ${node.files_count > 0 ? `<span class="text-[11px] text-gray-400">(${node.files_count})</span>` : ""}
        </label>
      `

      container.appendChild(div)

      if (node.children && node.children.length > 0) {
        this._renderExportNodes(node.children, container, depth + 1)
      }
    })
  }

  toggleExportAll(event) {
    const checked = event.target.checked
    this.exportTreeTarget.querySelectorAll(".export-folder-cb").forEach(cb => {
      cb.checked = checked
    })
  }

  toggleExportFolder(event) {
    // When a parent is toggled, toggle all children too
    const checked = event.target.checked
    const nodeId = event.target.value
    const node = this._findNodeById(this._parseNodeId(nodeId), this._treeData)
    if (!node || !node.children) return

    const childIds = this._collectAllIds(node.children)
    const checkboxes = this.exportTreeTarget.querySelectorAll(".export-folder-cb")
    checkboxes.forEach(cb => {
      if (childIds.includes(this._parseNodeId(cb.value))) {
        cb.checked = checked
      }
    })

    this._syncSelectAll()
  }

  _syncSelectAll() {
    const checkboxes = this.exportTreeTarget.querySelectorAll(".export-folder-cb")
    const allChecked = Array.from(checkboxes).every(cb => cb.checked)
    this.exportSelectAllTarget.checked = allChecked
  }

  async submitExport() {
    const checkboxes = this.exportTreeTarget.querySelectorAll(".export-folder-cb:checked")
    const selectedFolderIds = Array.from(checkboxes).map(cb => parseInt(cb.value))

    if (selectedFolderIds.length === 0) {
      this._showToast(this.i18nSelectOneFolderValue, "error")
      return
    }

    // Build export payload: for each selected folder, include all its files
    const token = document.querySelector('meta[name="csrf-token"]')?.content
    const toExport = []

    // Fetch files for each selected folder
    for (const folderId of selectedFolderIds) {
      try {
        const res = await fetch(`${this.filesUrlValue}?folder_id=${folderId}`, {
          headers: { "Accept": "application/json", "X-CSRF-Token": token },
          credentials: "same-origin"
        })
        if (res.ok) {
          const files = await res.json()
          files.forEach(f => {
            toExport.push({
              attachment_id: f.id,
              folder_id: folderId,
              folder_name: this._findNodeById(folderId, this._treeData)?.name || ""
            })
          })
        }
      } catch { /* skip */ }
    }

    if (toExport.length === 0) {
      this._showToast(this.i18nNoFilesToExportValue, "error")
      return
    }

    try {
      this.exportSubmitBtnTarget.disabled = true
      const res = await fetch(this.exportDocumentsUrlValue, {
        method: "POST",
        headers: { "Content-Type": "application/json", "X-CSRF-Token": token },
        credentials: "same-origin",
        body: JSON.stringify({ to_export: JSON.stringify(toExport) })
      })

      if (!res.ok) throw new Error("Export failed")

      this.closeExportModal()
      this._showToast(this.i18nExportQueuedValue)
    } catch (e) {
      console.error("[Documents] Export failed:", e)
      this._showToast(e.message || "Export failed", "error")
    } finally {
      this.exportSubmitBtnTarget.disabled = false
    }
  }

  // ── Reload Folder Structure ───────────────────────────────────

  openReloadStructureModal(event) {
    event.preventDefault()
    this.reloadStructureModalTarget.classList.remove("hidden")
  }

  closeReloadStructureModal() {
    this.reloadStructureModalTarget.classList.add("hidden")
  }

  async confirmReloadStructure() {
    const token = document.querySelector('meta[name="csrf-token"]')?.content

    try {
      const res = await fetch(this.reloadFolderStructureUrlValue, {
        method: "POST",
        headers: { "Content-Type": "application/json", "X-CSRF-Token": token },
        credentials: "same-origin"
      })

      if (!res.ok) throw new Error("Failed to generate default folders")

      // Seeded folders are server-rendered into the tree on load.
      window.location.reload()
    } catch (e) {
      console.error("[Documents] Generate default folders failed:", e)
      this.closeReloadStructureModal()
      this._showToast(e.message || "Failed to generate default folders", "error")
    }
  }

  // ── Panel Resize ──────────────────────────────────────────────

  startResize(event) {
    event.preventDefault()
    // Clean up any previous listeners to prevent accumulation
    if (this._onResizeMove) document.removeEventListener("mousemove", this._onResizeMove)
    if (this._onResizeEnd) document.removeEventListener("mouseup", this._onResizeEnd)
    this._resizing = true
    this._resizeStartX = event.clientX
    this._resizeStartWidth = this.folderPanelTarget.offsetWidth

    this._onResizeMove = (e) => {
      if (!this._resizing) return
      const delta = e.clientX - this._resizeStartX
      const newWidth = Math.max(180, Math.min(600, this._resizeStartWidth + delta))
      this.folderPanelTarget.style.width = `${newWidth}px`
    }

    this._onResizeEnd = () => {
      this._resizing = false
      this.panelDividerTarget.classList.remove("bg-brand")
      document.removeEventListener("mousemove", this._onResizeMove)
      document.removeEventListener("mouseup", this._onResizeEnd)
    }

    this.panelDividerTarget.classList.add("bg-brand")
    document.addEventListener("mousemove", this._onResizeMove)
    document.addEventListener("mouseup", this._onResizeEnd)
  }

  // ── Page-Level Drag ───────────────────────────────────────────

  _isUploadModalOpen() {
    return this.hasUploadModalTarget && !this.uploadModalTarget.classList.contains("hidden")
  }

  _setupPageDrag() {
    this._onDragOver = (e) => { e.preventDefault() }
    this._onDrop = (e) => {
      e.preventDefault()
      if (this._isUploadModalOpen()) return
      if (e.dataTransfer?.files?.length > 0) {
        if (this._selectedFolder && !this._selectedFolder.is_re_stage_folder) {
          this.openUploadModal()
          setTimeout(() => this._addUploadFiles(e.dataTransfer.files), 100)
        } else {
          this._showToast(this.i18nSelectFolderFirstValue, "error")
        }
      }
    }

    document.addEventListener("dragover", this._onDragOver)
    document.addEventListener("drop", this._onDrop)
  }

  _teardownPageDrag() {
    if (this._onDragOver) document.removeEventListener("dragover", this._onDragOver)
    if (this._onDrop) document.removeEventListener("drop", this._onDrop)
  }

  // ── Helpers ───────────────────────────────────────────────────

  _showFilePanel(state) {
    this.noFolderSelectedTarget.classList.add("hidden")
    this.noFolderSelectedTarget.style.display = ""
    this.emptyFilesTarget.classList.add("hidden")
    this.fileTableWrapperTarget.classList.add("hidden")
    this.subFoldersTarget.classList.add("hidden")
    this.fileLoadingTarget.classList.add("hidden")

    if (state === "noFolder") {
      this.noFolderSelectedTarget.classList.remove("hidden")
      this.breadcrumbsTarget.innerHTML = '<span class="text-gray-400">Select a folder</span>'
    }
  }

  _collectPrivacySettings(prefix) {
    // Builder projects (items 15-16): tokens come off the share-with-team
    // list. A fully-selected company stores ONE "company:<id>" token (so
    // people added to that company later are covered too); a partial
    // selection stores the individual "user:<id>" tokens.
    const shareList = prefix === "add"
      ? (this.hasAddShareListTarget ? this.addShareListTarget : null)
      : (this.hasEditShareListTarget ? this.editShareListTarget : null)
    if (shareList) {
      const tokens = []
      shareList.querySelectorAll("[data-share-company-box]").forEach(companyCb => {
        const members = this._shareMembersOf(shareList, companyCb.dataset.shareCompanyBox)
        if (companyCb.checked) {
          tokens.push(companyCb.dataset.shareToken)
        } else if (members.length > 0) {
          members.filter(cb => cb.checked).forEach(cb => tokens.push(cb.dataset.shareToken))
        }
      })
      return tokens.length > 0 ? tokens : ["private"]
    }

    const settings = []
    const targets = {
      consultants: prefix === "add" ? this.addPrivacyConsultantsTarget : this.editPrivacyConsultantsTarget,
      contractors: prefix === "add" ? this.addPrivacyContractorsTarget : this.editPrivacyContractorsTarget,
      other_vendors: prefix === "add" ? this.addPrivacyOtherVendorsTarget : this.editPrivacyOtherVendorsTarget,
      collaborators: prefix === "add" ? this.addPrivacyCollaboratorsTarget : this.editPrivacyCollaboratorsTarget
    }

    for (const [key, target] of Object.entries(targets)) {
      if (target?.checked) settings.push(key)
    }

    return settings.length > 0 ? settings : ["private"]
  }

  _findNodeById(id, nodes) {
    for (const node of nodes) {
      if (node.id === id) return node
      if (node.children) {
        const found = this._findNodeById(id, node.children)
        if (found) return found
      }
    }
    return null
  }

  _removeNodeById(id, nodes) {
    for (let i = 0; i < nodes.length; i++) {
      if (nodes[i].id === id) {
        nodes.splice(i, 1)
        return true
      }
      if (nodes[i].children && this._removeNodeById(id, nodes[i].children)) {
        return true
      }
    }
    return false
  }

  _collectAllIds(nodes) {
    const ids = []
    nodes.forEach(n => {
      ids.push(n.id)
      if (n.children) ids.push(...this._collectAllIds(n.children))
    })
    return ids
  }

  // Folders that have subfolders (the ones a chevron opens).
  _collectParentIds(nodes) {
    const ids = []
    nodes.forEach(n => {
      if (n.children?.length) ids.push(n.id, ...this._collectParentIds(n.children))
    })
    return ids
  }

  _parseNodeId(val) {
    const str = String(val)
    if (str.startsWith("re_stage_")) return str
    return parseInt(str)
  }

  _updateFolderCount(folderId, delta) {
    const node = this._findNodeById(folderId, this._treeData)
    if (node) {
      node.files_count = (node.files_count || 0) + delta
      if (node.files_count < 0) node.files_count = 0
    }
  }

  // Safe in BOTH text and attribute position — quotes must be escaped too,
  // or a value carrying an inch mark (`6" VERTICAL CURB`) closes its own
  // attribute and swallows the rest of the tag, data-* attributes included.
  _escapeHtml(str) {
    if (!str) return ""
    const div = document.createElement("div")
    div.textContent = str
    return div.innerHTML.replace(/"/g, "&quot;").replace(/'/g, "&#39;")
  }

  _formatFileSize(bytes) {
    if (bytes < 1024) return `${bytes} B`
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
  }

  _showToast(message, type = "success") {
    const cssType = (type === "error" || type === "alert") ? "alert" : "notice"
    const icon = cssType === "notice"
      ? '<svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="w-5 h-5"><path stroke-linecap="round" stroke-linejoin="round" d="M9 12.75 11.25 15 15 9.75M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0Z"/></svg>'
      : '<svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="w-5 h-5"><path stroke-linecap="round" stroke-linejoin="round" d="M12 9v3.75m9-.75a9 9 0 1 1-18 0 9 9 0 0 1 18 0Zm-9 3.75h.008v.008H12v-.008Z"/></svg>'
    const el = document.createElement("div")
    el.className = `flash-toast-${cssType}`
    el.setAttribute("data-controller", "flash")
    el.setAttribute("data-flash-dismiss-after-value", "5000")
    el.setAttribute("role", "alert")
    const iconDiv = document.createElement("div")
    iconDiv.className = "shrink-0"
    iconDiv.innerHTML = icon
    const msgP = document.createElement("p")
    msgP.className = "flex-1 text-sm"
    msgP.textContent = message
    el.appendChild(iconDiv)
    el.appendChild(msgP)
    document.body.prepend(el)
  }

  // Lucide outline icons — all gray, visually distinct by shape
  _fileTypeIcon(ext, sizeClass = "w-5 h-5") {
    ext = (ext || "").toLowerCase()
    const cls = `${sizeClass} text-gray-400`
    const base = `<svg class="${cls}" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">`

    // PDF / Documents with text lines (file-text)
    if (ext === "pdf" || ["doc","docx","txt"].includes(ext)) {
      return `${base}<path d="M6 22a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h8a2.4 2.4 0 0 1 1.704.706l3.588 3.588A2.4 2.4 0 0 1 20 8v12a2 2 0 0 1-2 2z"/><path d="M14 2v5a1 1 0 0 0 1 1h5"/><path d="M10 9H8"/><path d="M16 13H8"/><path d="M16 17H8"/></svg>`
    }
    // Images (file-image)
    if (["jpg","jpeg","gif","png","tiff","bmp","webp","heic","heif","dng"].includes(ext)) {
      return `${base}<path d="M6 22a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h8a2.4 2.4 0 0 1 1.704.706l3.588 3.588A2.4 2.4 0 0 1 20 8v12a2 2 0 0 1-2 2z"/><path d="M14 2v5a1 1 0 0 0 1 1h5"/><circle cx="10" cy="12" r="2"/><path d="m20 17-1.296-1.296a2.41 2.41 0 0 0-3.408 0L9 22"/></svg>`
    }
    // Spreadsheets (file-spreadsheet)
    if (["xls","xlsx","xlsm"].includes(ext)) {
      return `${base}<path d="M6 22a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h8a2.4 2.4 0 0 1 1.704.706l3.588 3.588A2.4 2.4 0 0 1 20 8v12a2 2 0 0 1-2 2z"/><path d="M14 2v5a1 1 0 0 0 1 1h5"/><path d="M8 13h2"/><path d="M14 13h2"/><path d="M8 17h2"/><path d="M14 17h2"/></svg>`
    }
    // Archives (file-archive)
    if (["zip","rar"].includes(ext)) {
      return `${base}<path d="M13.659 22H18a2 2 0 0 0 2-2V8a2.4 2.4 0 0 0-.706-1.706l-3.588-3.588A2.4 2.4 0 0 0 14 2H6a2 2 0 0 0-2 2v11.5"/><path d="M14 2v5a1 1 0 0 0 1 1h5"/><path d="M8 12v-1"/><path d="M8 18v-2"/><path d="M8 7V6"/><circle cx="8" cy="20" r="2"/></svg>`
    }
    // Presentations (presentation)
    if (["ppt","pptx"].includes(ext)) {
      return `${base}<path d="M2 3h20"/><path d="M21 3v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V3"/><path d="m7 21 5-5 5 5"/></svg>`
    }
    // Generic file
    return `${base}<path d="M6 22a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h8a2.4 2.4 0 0 1 1.704.706l3.588 3.588A2.4 2.4 0 0 1 20 8v12a2 2 0 0 1-2 2z"/><path d="M14 2v5a1 1 0 0 0 1 1h5"/></svg>`
  }

  // ── Doc-Sync chip strip (§ 3.8 Tier 6 Step 74) ─────────────────
  // Renders a per-connection chip below the filename. Status →
  // colour: success (blue), pending/failed (gray), stale/conflict
  // (amber). Click → opens the per-attachment sync history modal.
  _renderSyncChips(file) {
    const links = Array.isArray(file.sync_links) ? file.sync_links : []
    if (links.length === 0) return ""
    const chips = links.map(link => this._renderSyncChip(link, file)).join("")
    return `<div class="mt-1 flex flex-wrap items-center gap-1">${chips}</div>`
  }

  _renderSyncChip(link, file) {
    const status = (link.status || "pending").toString()
    let cls, label
    if (link.stale || link.conflict) {
      cls = "bg-amber-50 text-amber-800 border-amber-200"
      label = "Conflict — review"
    } else if (status === "success" || status === "manual") {
      cls = "bg-blue-50 text-blue-800 border-blue-200"
      label = "Synced"
    } else if (status === "failed") {
      cls = "bg-rose-50 text-rose-800 border-rose-200"
      label = "Sync failed"
    } else if (status === "skipped") {
      cls = "bg-gray-50 text-gray-600 border-gray-200"
      label = "Skipped"
    } else {
      cls = "bg-gray-50 text-gray-600 border-gray-200"
      label = "Pending sync"
    }
    const provider = this._escapeHtml(link.provider_name || link.provider_slug || "Cloud")
    // _escapeHtml only escapes <, >, &, not " — and JSON.stringify
    // always emits ". encodeURIComponent dodges every HTML-special char
    // so the data-attribute survives a filename like
    // `"; onclick="alert(1)`. The handler decodes on read.
    const payload = encodeURIComponent(JSON.stringify({ file_id: file.id, filename: file.filename, link: link }))
    return `<button type="button"
                    data-action="click->documents#openSyncHistoryModal"
                    data-payload="${payload}"
                    class="inline-flex items-center gap-1 px-2 py-0.5 rounded-md border ${cls} text-[11px] font-medium hover:brightness-95 cursor-pointer">
      <span class="font-semibold">${provider}</span>
      <span class="text-gray-500">·</span>
      <span>${label}</span>
    </button>`
  }

  // Opens the sync history modal for one attachment-connection
  // tuple. The modal lives in `index.html.erb` (target
  // `syncHistoryModal`). Payload comes from the chip's data-payload
  // (passed JSON-encoded so we can render Local vs Cloud + the three
  // resolver buttons inline without a server round-trip).
  openSyncHistoryModal(event) {
    if (!this.hasSyncHistoryModalTarget) return
    const raw = event.currentTarget?.dataset?.payload
    let data = null
    try { data = JSON.parse(decodeURIComponent(raw || "")) } catch (_) { return }
    if (!data) return

    const link = data.link || {}
    const filename = data.filename || ""
    const isStale = !!(link.stale || link.conflict)
    const status = (link.status || "pending").toString()
    const lastSynced = link.last_synced_at ? this._timeAgo(link.last_synced_at) : "never"
    const provider = this._escapeHtml(link.provider_name || link.provider_slug || "Cloud")
    const connectionName = this._escapeHtml(link.connection_name || "")
    const webUrl = link.web_url || ""

    const headerStatusClass = isStale
      ? "bg-amber-50 text-amber-800 border-amber-200"
      : status === "success"
        ? "bg-blue-50 text-blue-800 border-blue-200"
        : status === "failed"
          ? "bg-rose-50 text-rose-800 border-rose-200"
          : "bg-gray-50 text-gray-600 border-gray-200"

    const error = link.last_sync_error
      ? `<div class="mt-2 text-[12px] text-rose-700 font-mono">${this._escapeHtml(link.last_sync_error)}</div>`
      : ""

    const resolver = isStale ? this._renderConflictResolver(link, filename) : ""

    const cloudOpenLink = webUrl
      ? `<a href="${this._escapeHtml(webUrl)}" target="_blank" rel="noopener" class="text-brand hover:underline text-[13px]">Open in ${provider} →</a>`
      : `<span class="text-[12px] text-gray-400">No deep link available.</span>`

    this.syncHistoryBodyTarget.innerHTML = `
      <div class="px-5 py-4 border-b border-gray-200">
        <div class="flex items-start justify-between gap-3">
          <div class="min-w-0">
            <h3 class="text-[15px] font-semibold text-gray-900 truncate" title="${this._escapeHtml(filename)}">${this._escapeHtml(filename)}</h3>
            <p class="text-[12px] text-gray-500 mt-0.5">
              ${provider} · <span class="font-mono">${connectionName}</span>
            </p>
          </div>
          <button type="button" data-action="click->documents#closeSyncHistoryModal"
                  class="text-gray-400 hover:text-gray-600 cursor-pointer p-1">
            <svg class="w-5 h-5" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" d="M6 18L18 6M6 6l12 12"/></svg>
          </button>
        </div>
      </div>
      <div class="px-5 py-4 space-y-3">
        <div class="flex items-center gap-2 flex-wrap">
          <span class="inline-flex items-center px-2 py-0.5 rounded-md border ${headerStatusClass} text-[11px] font-medium">
            ${this._escapeHtml(status)}
          </span>
          <span class="text-[12px] text-gray-500">Last synced: ${this._escapeHtml(lastSynced)}</span>
          <span class="ml-auto">${cloudOpenLink}</span>
        </div>
        ${error}
        ${resolver}
      </div>
    `
    this.syncHistoryModalTarget.classList.remove("hidden")
  }

  closeSyncHistoryModal() {
    if (this.hasSyncHistoryModalTarget) this.syncHistoryModalTarget.classList.add("hidden")
  }

  _renderConflictResolver(link, filename) {
    const externalIdId = link.external_id_id
    if (!externalIdId) return ""
    const url = `/integration_conflicts/${externalIdId}/resolve`
    return `
      <div class="mt-2 rounded-lg border border-amber-200 bg-amber-50/40 p-3">
        <h4 class="text-[13px] font-semibold text-amber-900">This file changed on both sides since the last sync.</h4>
        <p class="text-[12px] text-amber-800/90 mt-0.5">
          Pick which copy to keep. ConsLog will record the resolution and resume normal sync.
        </p>
        <div class="mt-3 grid grid-cols-1 md:grid-cols-2 gap-3">
          <div class="rounded-md border border-gray-200 bg-white p-3">
            <div class="text-[11px] font-semibold text-gray-500 uppercase tracking-wide">Local (ConsLog)</div>
            <p class="text-[13px] text-gray-800 mt-1 break-words">${this._escapeHtml(filename)}</p>
            <button type="button" data-action="click->documents#resolveConflict"
                    data-resolve-url="${url}" data-resolution="keep_local"
                    class="mt-2 w-full px-3 py-1.5 text-[12px] font-medium rounded-lg bg-brand hover:bg-brand-dark text-white cursor-pointer">
              Keep ConsLog → push
            </button>
          </div>
          <div class="rounded-md border border-gray-200 bg-white p-3">
            <div class="text-[11px] font-semibold text-gray-500 uppercase tracking-wide">Cloud</div>
            <p class="text-[13px] text-gray-800 mt-1 break-words">${this._escapeHtml(filename)}</p>
            <button type="button" data-action="click->documents#resolveConflict"
                    data-resolve-url="${url}" data-resolution="take_remote"
                    class="mt-2 w-full px-3 py-1.5 text-[12px] font-medium rounded-lg bg-white border border-gray-300 text-gray-700 hover:bg-gray-50 cursor-pointer">
              Take Cloud → pull
            </button>
          </div>
        </div>
        <div class="mt-3 flex items-center justify-end">
          <button type="button" data-action="click->documents#resolveConflict"
                  data-resolve-url="${url}" data-resolution="dismiss"
                  class="text-[12px] text-gray-500 hover:underline cursor-pointer">
            Dismiss without changing either side
          </button>
        </div>
      </div>
    `
  }

  resolveConflict(event) {
    const btn = event.currentTarget
    const url = btn?.dataset?.resolveUrl
    const resolution = btn?.dataset?.resolution
    if (!url || !resolution) return
    btn.disabled = true
    btn.textContent = "Working…"
    fetch(url, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "Accept": "application/json",
        "X-CSRF-Token": this._csrfToken()
      },
      body: JSON.stringify({ resolution })
    })
      .then(r => r.json())
      .then(json => {
        if (json.ok) {
          this._showToast(`Conflict resolved (${this._escapeHtml(resolution)}).`, "success")
          this.closeSyncHistoryModal()
          this._reloadCurrentFolder()
        } else {
          this._showToast(json.message || "Couldn't resolve — try again.", "error")
          btn.disabled = false
          btn.textContent = resolution.replace("_", " ")
        }
      })
      .catch(() => {
        this._showToast("Network error — try again.", "error")
        btn.disabled = false
      })
  }

  _csrfToken() {
    const m = document.querySelector('meta[name="csrf-token"]')
    return m?.content || ""
  }

  _timeAgo(iso) {
    try {
      const d = new Date(iso)
      const diff = Math.floor((Date.now() - d.getTime()) / 1000)
      if (diff < 60) return `${diff}s ago`
      if (diff < 3600) return `${Math.floor(diff / 60)}m ago`
      if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`
      return `${Math.floor(diff / 86400)}d ago`
    } catch (_) {
      return iso
    }
  }

  _reloadCurrentFolder() {
    if (this._selectedFolder?.id) {
      this.selectFolder({ currentTarget: { dataset: { nodeId: this._selectedFolder.id } } })
    }
  }
}
